import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, User, XCircle, CheckCircle, Upload, Heart, MapPin, Phone, Calendar, Users, BookOpen, FileText, MessageSquare, Bell, Info } from 'lucide-react';
import PhotoCapture from '@/components/PhotoCapture';
import ChildAvatar from '@/components/ChildAvatar';
import { useAuth } from '@/contexts/AuthContext';
import { matchesSearch } from '@/utils/searchUtils';

const Children = () => {
  const { canEdit, isAdmin } = useAuth();
  const canEditChildren = canEdit('children') || isAdmin();
  
  const [children, setChildren] = useState([]);
  const [allChildren, setAllChildren] = useState([]); // Todas as crianças (ativas e inativas) para cálculo de matrícula
  const [filteredChildren, setFilteredChildren] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deactivateDialogOpen, setDeactivateDialogOpen] = useState(false);
  const [selectedChild, setSelectedChild] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [viewingChild, setViewingChild] = useState(null);
  const [editingChild, setEditingChild] = useState(null);
  const [showInactive, setShowInactive] = useState(false);
  
  // Relatos
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [reportContent, setReportContent] = useState('');
  const [childReports, setChildReports] = useState([]);
  const [unreadReportsCount, setUnreadReportsCount] = useState(0);
  const [unreadReportsChildIds, setUnreadReportsChildIds] = useState([]);
  const [reportsListOpen, setReportsListOpen] = useState(false);
  
  // Filtros
  const [filters, setFilters] = useState({
    search: '',
    origin: '',
    gender: '',
    days: '',
    ages: [], // Filtro de múltipla escolha por idade
    sortOrder: 'asc',
    sortBy: 'name',
    shift: ''
  });
  
  const [formData, setFormData] = useState({
    name: '',
    birth_date: '',
    gender: 'MASCULINO',
    photo_url: '',
    enrollment_origin: 'livre_demanda',
    enrollment_number: '',
    enrollment_status: 'Ativo',
    group_name: '',
    shift: 'Vespertino',
    days_of_week: [],
    school: '',
    enrollment_date: new Date().toISOString().split('T')[0],
    insertion_reason: '',
    neighborhood: '',
    guardian_name: '',
    guardian_phone: '',
    guardian_relationship: '',
    address: '',
    related_children: [],
    selected_activities: []  // Atividades escolhidas para geração de grade
  });
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [spreadsheetUrl, setSpreadsheetUrl] = useState('');
  const [deactivationData, setDeactivationData] = useState({
    deactivation_date: new Date().toISOString().split('T')[0],
    deactivation_reason: ''
  });
  const [activities, setActivities] = useState([]);
  const [selectedActivities, setSelectedActivities] = useState([]);
  const [allowRepeatForActivity, setAllowRepeatForActivity] = useState({});
  const [showCustomRelationship, setShowCustomRelationship] = useState(false);

  const daysOfWeek = ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
  const standardRelationships = ['Mãe', 'Pai', 'Avó', 'Avô', 'Tio', 'Tia'];

  useEffect(() => {
    fetchChildren();
    fetchActivities();
    fetchUnreadReportsCount();
  }, [showInactive]);

  useEffect(() => {
    applyFilters(children);
  }, [filters, children]);

  // Connect searchTerm to filters.search
  useEffect(() => {
    setFilters(prev => ({ ...prev, search: searchTerm }));
  }, [searchTerm]);

  const fetchChildren = async () => {
    try {
      // Pass active_only=false to get ALL children (active and inactive)
      const response = await axios.get(`${API}/children?active_only=false`);
      let childrenData = response.data;
      
      // Guardar TODAS as crianças para cálculo de matrícula
      setAllChildren(childrenData);
      
      // Filtrar por status ativo/inativo
      if (showInactive) {
        // Mostrar APENAS inativas
        childrenData = childrenData.filter(c => !c.is_active);
      } else {
        // Mostrar APENAS ativas
        childrenData = childrenData.filter(c => c.is_active);
      }
      
      setChildren(childrenData);
      applyFilters(childrenData);
    } catch (error) {
      toast.error('Erro ao carregar crianças');
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = (data = children) => {
    let filtered = [...data];

    // Busca por nome
    if (filters.search) {
      filtered = filtered.filter(c => 
        matchesSearch(c.name, filters.search)
      );
    }

    // Filtro por origem
    if (filters.origin) {
      filtered = filtered.filter(c => c.enrollment_origin === filters.origin);
    }

    // Filtro por gênero
    if (filters.gender) {
      filtered = filtered.filter(c => c.gender === filters.gender);
    }

    // Filtro por turno
    if (filters.shift) {
      filtered = filtered.filter(c => c.shift === filters.shift);
    }

    // Filtro por dias
    if (filters.days) {
      if (filters.days === 'ter_qui') {
        // Ter e Qui: crianças que têm terça E quinta
        filtered = filtered.filter(c => 
          c.days_of_week && c.days_of_week.includes('terca') && c.days_of_week.includes('quinta')
        );
      } else if (filters.days === 'qua_sex') {
        // Qua e Sexta: crianças que têm quarta E sexta
        filtered = filtered.filter(c => 
          c.days_of_week && c.days_of_week.includes('quarta') && c.days_of_week.includes('sexta')
        );
      } else if (filters.days === 'todos') {
        // Todos os dias (seg a sex): crianças que têm os 5 dias
        filtered = filtered.filter(c => 
          c.days_of_week && 
          c.days_of_week.includes('segunda') &&
          c.days_of_week.includes('terca') &&
          c.days_of_week.includes('quarta') &&
          c.days_of_week.includes('quinta') &&
          c.days_of_week.includes('sexta')
        );
      }
    }

    // Filtro por idade (múltipla escolha)
    if (filters.ages && filters.ages.length > 0) {
      filtered = filtered.filter(c => filters.ages.includes(c.age));
    }

    // Ordenação
    filtered.sort((a, b) => {
      if (filters.sortBy === 'name') {
        if (filters.sortOrder === 'asc') {
          return a.name.localeCompare(b.name);
        } else {
          return b.name.localeCompare(a.name);
        }
      } else if (filters.sortBy === 'age') {
        if (filters.sortOrder === 'asc') {
          return (a.age || 0) - (b.age || 0);
        } else {
          return (b.age || 0) - (a.age || 0);
        }
      }
      return 0;
    });

    setFilteredChildren(filtered);
  };

  const fetchActivities = async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data);
    } catch (error) {
      console.error('Erro ao carregar atividades:', error);
    }
  };

  const fetchUnreadReportsCount = async () => {
    try {
      const response = await axios.get(`${API}/children/reports/unread-count`);
      setUnreadReportsCount(response.data.count || 0);
      setUnreadReportsChildIds(response.data.child_ids || []);
    } catch (error) {
      console.log('Erro ao carregar contagem de relatos');
    }
  };

  const fetchChildReports = async (childId) => {
    try {
      const response = await axios.get(`${API}/children/${childId}/reports`);
      setChildReports(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar relatos');
    }
  };

  const submitReport = async () => {
    if (!viewingChild || !reportContent.trim()) {
      toast.error('Digite o conteúdo do relato');
      return;
    }
    
    try {
      await axios.post(`${API}/children/${viewingChild.id}/reports`, {
        content: reportContent
      });
      toast.success('Relato registrado com sucesso!');
      setReportContent('');
      setReportDialogOpen(false);
      fetchChildReports(viewingChild.id);
      fetchUnreadReportsCount();
    } catch (error) {
      toast.error('Erro ao registrar relato');
    }
  };

  const markReportAsRead = async (reportId) => {
    try {
      await axios.post(`${API}/children/reports/${reportId}/mark-read`);
      fetchUnreadReportsCount();
    } catch (error) {
      console.log('Erro ao marcar relato como lido');
    }
  };

  const deleteReport = async (reportId) => {
    if (!confirm('Tem certeza que deseja excluir este relato?')) return;
    
    try {
      await axios.delete(`${API}/children/reports/${reportId}`);
      toast.success('Relato excluído');
      if (viewingChild) {
        fetchChildReports(viewingChild.id);
      }
      fetchUnreadReportsCount();
    } catch (error) {
      toast.error('Erro ao excluir relato');
    }
  };

  const getFilteredActivities = () => {
    if (!formData.shift || formData.days_of_week.length === 0) {
      return [];
    }
    
    return activities.filter(activity => {
      // Verificar se atividade está disponível no turno e dias da criança
      // Suporta formato novo (schedule) e formato antigo (shift/shifts + days_of_week)
      
      if (activity.schedule && typeof activity.schedule === 'object' && Object.keys(activity.schedule).length > 0) {
        // Formato novo: schedule = { "terca": ["Matutino", "Vespertino"], ... }
        return formData.days_of_week.some(day => {
          const shiftsForDay = activity.schedule[day];
          return shiftsForDay && shiftsForDay.includes(formData.shift);
        });
      } else {
        // Formato antigo: shift/shifts + days_of_week
        const activityShifts = activity.shifts && activity.shifts.length > 0 
          ? activity.shifts 
          : (activity.shift ? [activity.shift] : []);
        const activityDays = activity.days_of_week || [];
        
        // Verificar turno
        if (!activityShifts.includes(formData.shift)) {
          return false;
        }
        
        // Verificar dias - pelo menos um dia em comum
        return activityDays.some(day => formData.days_of_week.includes(day));
      }
    });
  };

  const getAvailableDays = (origin) => {
    switch(origin) {
      case 'livre_demanda':
        return ['segunda', 'terca', 'quarta', 'quinta', 'sexta'];
      case 'gov_celso_ramos':
        return ['terca', 'quinta'];
      case 'biguacu':
        return ['terca', 'quinta', 'quarta', 'sexta'];
      default:
        return [];
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.days_of_week.length === 0) {
      toast.error('Selecione pelo menos um dia da semana');
      return;
    }

    try {
      let childId;
      
      // CRÍTICO: Incluir selected_activities no formData antes de salvar
      const dataToSave = {
        ...formData,
        selected_activities: selectedActivities  // Sincronizar as atividades escolhidas
      };
      
      // Verificar se as atividades foram alteradas (somente para edição)
      const originalActivities = editingChild?.selected_activities || [];
      const activitiesChanged = editingChild ? (
        selectedActivities.length !== originalActivities.length ||
        !selectedActivities.every(id => originalActivities.includes(id)) ||
        !originalActivities.every(id => selectedActivities.includes(id))
      ) : true; // Para novo cadastro, sempre considerar como "alterado"
      
      if (editingChild) {
        await axios.put(`${API}/children/${editingChild.id}`, dataToSave);
        childId = editingChild.id;
        toast.success('Criança atualizada com sucesso!');
      } else {
        const response = await axios.post(`${API}/children`, dataToSave);
        childId = response.data.id;
        toast.success('Criança cadastrada com sucesso!');
      }

      // CORREÇÃO: Só criar/refazer matrículas se:
      // 1. É um novo cadastro E tem atividades selecionadas, OU
      // 2. É uma edição E as atividades foram REALMENTE alteradas
      if (!editingChild && selectedActivities.length > 0) {
        // Novo cadastro com atividades - criar matrículas
        const enrollmentData = {
          child_id: childId,
          activities: selectedActivities.map(actId => ({
            activity_id: actId,
            frequency: formData.days_of_week.length,
            allow_repeat: allowRepeatForActivity[actId] || false
          }))
        };
        
        await axios.post(`${API}/enrollments`, enrollmentData);
        toast.success(`${selectedActivities.length} atividade(s) matriculada(s)!`);
      } else if (editingChild && activitiesChanged) {
        // Edição com atividades alteradas - refazer matrículas
        // Limpar matrículas antigas
        try {
          await axios.delete(`${API}/enrollments/child/${childId}`);
        } catch (err) {
          console.log('Sem matrículas antigas para limpar');
        }
        
        // Criar novas matrículas apenas se há atividades selecionadas
        if (selectedActivities.length > 0) {
          const enrollmentData = {
            child_id: childId,
            activities: selectedActivities.map(actId => ({
              activity_id: actId,
              frequency: formData.days_of_week.length,
              allow_repeat: allowRepeatForActivity[actId] || false
            }))
          };
          
          await axios.post(`${API}/enrollments`, enrollmentData);
          toast.success(`${selectedActivities.length} atividade(s) matriculada(s)!`);
        } else {
          toast.info('Atividades removidas da criança');
        }
      }
      // Se é edição e atividades NÃO mudaram, não faz nada com matrículas

      setDialogOpen(false);
      resetForm();
      await fetchChildren();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleDeactivate = async () => {
    if (!selectedChild) return;
    try {
      await axios.post(`${API}/children/${selectedChild.id}/deactivate`, deactivationData);
      toast.success('Matrícula desativada com sucesso!');
      setDeactivateDialogOpen(false);
      fetchChildren();
    } catch (error) {
      toast.error('Erro ao desativar matrícula');
    }
  };

  const handleReactivate = async (childId) => {
    try {
      await axios.post(`${API}/children/${childId}/reactivate`);
      toast.success('Matrícula reativada com sucesso!');
      fetchChildren();
    } catch (error) {
      toast.error('Erro ao reativar matrícula');
    }
  };

  const handleEdit = (child) => {
    setEditingChild(child);
    setFormData({
      ...child,
      days_of_week: child.days_of_week || [],
      selected_activities: child.selected_activities || [],
      related_children: child.related_children || []
    });
    // CRÍTICO: Sincronizar selectedActivities com o valor salvo da criança
    setSelectedActivities(child.selected_activities || []);
    // Verificar se o parentesco é customizado
    setShowCustomRelationship(
      child.guardian_relationship && !standardRelationships.includes(child.guardian_relationship)
    );
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir esta criança?')) {
      try {
        await axios.delete(`${API}/children/${id}`);
        toast.success('Criança removida com sucesso!');
        fetchChildren();
      } catch (error) {
        toast.error('Erro ao remover criança');
      }
    }
  };

  const resetForm = () => {
    // Calcular próximo número de matrícula
    const nextEnrollmentNumber = getNextEnrollmentNumber();
    
    setFormData({
      name: '',
      birth_date: '',
      gender: 'MASCULINO',
      enrollment_origin: 'livre_demanda',
      enrollment_number: nextEnrollmentNumber,
      enrollment_status: 'Ativo',
      group_name: '',
      shift: 'Vespertino',
      days_of_week: [],
      school: '',
      enrollment_date: new Date().toISOString().split('T')[0],
      insertion_reason: '',
      neighborhood: '',
      guardian_name: '',
      guardian_phone: '',
      guardian_relationship: '',
      address: '',
      related_children: [],
      selected_activities: []
    });
    setSelectedActivities([]);
    setAllowRepeatForActivity({});
    setEditingChild(null);
    setShowCustomRelationship(false);
  };

  // Função para calcular próximo número de matrícula
  const getNextEnrollmentNumber = () => {
    // Coletar todos os números de matrícula existentes (ativos e inativos)
    const usedNumbers = [];
    
    allChildren.forEach(child => {
      if (child.enrollment_number) {
        const numericPart = parseInt(child.enrollment_number.toString().replace(/\D/g, ''), 10);
        if (!isNaN(numericPart) && numericPart > 0) {
          usedNumbers.push(numericPart);
        }
      }
    });
    
    if (usedNumbers.length === 0) return '1';
    
    // Ordenar e ignorar outliers (números isolados muito acima da sequência)
    usedNumbers.sort((a, b) => a - b);
    
    // Encontrar o fim da sequência principal: se houver um salto > 100 entre consecutivos,
    // considerar tudo abaixo como a sequência normal
    let sequenceMax = usedNumbers[0];
    for (let i = 1; i < usedNumbers.length; i++) {
      if (usedNumbers[i] - usedNumbers[i - 1] > 100) {
        break; // Encontrou um salto grande, parar aqui
      }
      sequenceMax = usedNumbers[i];
    }
    
    // Próximo número sequencial
    const usedSet = new Set(usedNumbers);
    let next = sequenceMax + 1;
    while (usedSet.has(next)) {
      next++;
    }
    
    return next.toString();
  };

  const toggleDay = (day) => {
    const availableDays = getAvailableDays(formData.enrollment_origin);
    if (!availableDays.includes(day)) return;
    
    setFormData(prev => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(day)
        ? prev.days_of_week.filter(d => d !== day)
        : [...prev.days_of_week, day]
    }));
  };

  return (
    <div className="space-y-4 sm:space-y-6" data-testid="children-page">
      {/* Header com botões - sempre visível */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-800">Crianças</h1>
          <p className="text-sm text-slate-600 mt-1">Gerencie o cadastro de crianças</p>
        </div>
        
        {/* Botões principais */}
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          {/* Botão Ver Relatos - apenas para editores */}
          {canEditChildren && unreadReportsCount > 0 && (
            <Button
              variant="outline"
              size="sm"
              className="relative text-purple-600 border-purple-300 hover:bg-purple-50"
              onClick={() => setReportsListOpen(true)}
            >
              <Bell className="h-4 w-4 mr-1" />
              Relatos
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {unreadReportsCount > 9 ? '9+' : unreadReportsCount}
              </span>
            </Button>
          )}
          
          <Button
            variant={showInactive ? 'default' : 'outline'}
            onClick={() => setShowInactive(!showInactive)}
            data-testid="toggle-inactive"
            size="sm"
            className="flex-1 sm:flex-none"
          >
            {showInactive ? 'Ver Ativos' : 'Ver Inativos'}
          </Button>
          
          {/* Botão de Importação - apenas com permissão de edição */}
          {canEditChildren && (
            <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-spreadsheet-button" size="sm" className="flex-1 sm:flex-none">
                  <Upload className="h-4 w-4 mr-1 sm:mr-2" />
                  <span className="hidden sm:inline">Importar</span>
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Importar Crianças</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6">
                  {/* Opção 1: URL do Google Sheets */}
                  <div className="space-y-3">
                    <h3 className="font-semibold text-slate-800">Opção 1: Google Sheets</h3>
                    <div>
                      <Label htmlFor="spreadsheet-url">URL da Planilha</Label>
                      <Input
                        id="spreadsheet-url"
                        data-testid="spreadsheet-url-input"
                        placeholder="https://docs.google.com/spreadsheets/d/..."
                        value={spreadsheetUrl}
                        onChange={(e) => setSpreadsheetUrl(e.target.value)}
                      />
                    </div>
                    <Button 
                      className="w-full"
                      onClick={async () => {
                        if (!spreadsheetUrl) {
                          toast.error('Por favor, insira a URL da planilha');
                          return;
                        }
                        
                        try {
                          toast.info('Importando planilha... Por favor, aguarde.');
                          const response = await axios.post(`${API}/children/import-spreadsheet`, {
                            spreadsheet_url: spreadsheetUrl
                          });
                          
                          const { imported, skipped } = response.data;
                          toast.success(`✅ ${imported} crianças importadas, ${skipped} linhas ignoradas.`);
                          
                          setImportDialogOpen(false);
                          setSpreadsheetUrl('');
                          fetchChildren();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erro ao importar planilha');
                        }
                      }}
                      data-testid="import-url-button"
                    >
                      Importar do Google Sheets
                    </Button>
                  </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t border-slate-200" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-slate-500">OU</span>
                  </div>
                </div>

                {/* Opção 2: Upload de Arquivo */}
                <div className="space-y-3">
                  <h3 className="font-semibold text-slate-800">Opção 2: Upload de Arquivo</h3>
                  <div>
                    <Label htmlFor="file-upload">Arquivo CSV ou Excel</Label>
                    <Input
                      id="file-upload"
                      type="file"
                      accept=".csv,.xlsx,.xls"
                      data-testid="file-upload-input"
                      onChange={async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;
                        
                        try {
                          toast.info('Importando arquivo... Por favor, aguarde.');
                          
                          const formData = new FormData();
                          formData.append('file', file);
                          
                          const response = await axios.post(`${API}/children/import-file`, formData, {
                            headers: { 'Content-Type': 'multipart/form-data' }
                          });
                          
                          const { imported, skipped } = response.data;
                          toast.success(`✅ ${imported} crianças importadas, ${skipped} linhas ignoradas.`);
                          
                          setImportDialogOpen(false);
                          fetchChildren();
                        } catch (error) {
                          toast.error(error.response?.data?.detail || 'Erro ao importar arquivo');
                        }
                      }}
                    />
                    <p className="text-xs text-slate-500 mt-2">
                      Formatos aceitos: CSV, XLSX, XLS
                    </p>
                  </div>
                </div>

                {/* Informações */}
                <div className="space-y-2">
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                    <p className="text-xs text-yellow-800">
                      <strong>Google Sheets:</strong> A planilha precisa estar configurada como &quot;Qualquer pessoa com o link pode visualizar&quot;
                    </p>
                  </div>
                  
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                    <p className="text-xs text-blue-800">
                      <strong>Campos esperados:</strong> ALUNO, STATUS DE MATRÍCULA, TURMA, TURNO, ORIGEM, DIAS, GÊNERO, MATRÍCULA, DATA DE NASCIMENTO, TELEFONE, RESPONSÁVEL, PARENTESCO
                    </p>
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button type="button" variant="outline" onClick={() => setImportDialogOpen(false)}>
                    Fechar
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          )}

          {/* Botão Nova Criança - apenas com permissão de edição */}
          {canEditChildren && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { 
            setDialogOpen(open); 
            if (!open) {
              resetForm();
            } else if (!editingChild) {
              // Ao abrir para nova criança, preencher número de matrícula
              const nextNumber = getNextEnrollmentNumber();
              setFormData(prev => ({ ...prev, enrollment_number: nextNumber }));
            }
          }}>
            <DialogTrigger asChild>
              <Button data-testid="add-child-button" className="flex-1 sm:flex-none">
                <Plus className="h-4 w-4 mr-1 sm:mr-2" />
                Nova Criança
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingChild ? 'Editar Criança' : 'Nova Criança'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Foto */}
                <div className="border-b pb-4">
                  <Label>Foto da Criança</Label>
                  <PhotoCapture
                    currentPhoto={formData.photo_url}
                    onPhotoCapture={(photo) => setFormData({ ...formData, photo_url: photo })}
                    onPhotoRemove={() => setFormData({ ...formData, photo_url: '' })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="md:col-span-3">
                    <Label htmlFor="name">Nome Completo *</Label>
                    <Input
                      id="name"
                      data-testid="child-name-input"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="birth_date">Data de Nascimento *</Label>
                    <Input
                      id="birth_date"
                      type="date"
                      data-testid="child-birthdate-input"
                      value={formData.birth_date}
                      onChange={(e) => setFormData({ ...formData, birth_date: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="gender">Sexo *</Label>
                    <select
                      id="gender"
                      data-testid="child-gender-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.gender}
                      onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                    >
                      <option value="masculino">Masculino</option>
                      <option value="feminino">Feminino</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="enrollment_number">Número de Matrícula *</Label>
                    <Input
                      id="enrollment_number"
                      data-testid="enrollment-number-input"
                      value={formData.enrollment_number}
                      onChange={(e) => setFormData({ ...formData, enrollment_number: e.target.value })}
                      required
                      placeholder="Ex: 2025001"
                    />
                  </div>
                  <div>
                    <Label htmlFor="enrollment_origin">Origem da Matrícula *</Label>
                    <select
                      id="enrollment_origin"
                      data-testid="child-origin-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.enrollment_origin}
                      onChange={(e) => {
                        setFormData({ ...formData, enrollment_origin: e.target.value, days_of_week: [] });
                        if (e.target.value === 'livre_demanda') {
                          setFormData(prev => ({ ...prev, shift: 'Vespertino' }));
                        }
                      }}
                    >
                      <option value="livre_demanda">Livre Demanda</option>
                      <option value="gov_celso_ramos">Governador Celso Ramos</option>
                      <option value="biguacu">Biguaçu</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="shift">Turno *</Label>
                    <select
                      id="shift"
                      data-testid="child-shift-select"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                      value={formData.shift}
                      onChange={(e) => setFormData({ ...formData, shift: e.target.value })}
                      disabled={formData.enrollment_origin === 'livre_demanda'}
                    >
                      <option value="Matutino">Manhã</option>
                      <option value="Vespertino">Tarde</option>
                    </select>
                  </div>
                  <div>
                    <Label htmlFor="enrollment_date">Data de Inscrição</Label>
                    <Input
                      id="enrollment_date"
                      type="date"
                      data-testid="enrollment-date-input"
                      value={formData.enrollment_date}
                      onChange={(e) => setFormData({ ...formData, enrollment_date: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label>Dias da Semana *</Label>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {daysOfWeek.map(day => {
                      const availableDays = getAvailableDays(formData.enrollment_origin);
                      const isAvailable = availableDays.includes(day);
                      return (
                        <button
                          key={day}
                          type="button"
                          data-testid={`day-${day}`}
                          onClick={() => toggleDay(day)}
                          disabled={!isAvailable}
                          className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                            !isAvailable
                              ? 'bg-slate-100 text-slate-400 atividader-not-allowed'
                              : formData.days_of_week.includes(day)
                              ? 'bg-blue-500 text-white'
                              : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                          }`}
                        >
                          {day.charAt(0).toUpperCase() + day.slice(1)}
                        </button>
                      );
                    })}
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    {formData.enrollment_origin === 'livre_demanda' && 'Livre Demanda: Todos os dias à tarde'}
                    {formData.enrollment_origin === 'gov_celso_ramos' && 'Governador Celso Ramos: Terças e quintas'}
                    {formData.enrollment_origin === 'biguacu' && 'Biguaçu: Terças e quintas OU quartas e sextas'}
                  </p>
                </div>

                {/* Seleção de Atividades */}
                {formData.days_of_week.length > 0 && (
                  <div className="border-t pt-4">
                    <Label className="text-base font-semibold">Atividades Disponíveis</Label>
                    <p className="text-xs text-slate-500 mb-3">
                      Selecione as atividades que a criança participará (filtradas por turno e dias)
                    </p>
                    
                    {getFilteredActivities().length === 0 ? (
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 text-center text-slate-600">
                        Nenhuma atividade disponível para os critérios selecionados
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {getFilteredActivities().map(activity => (
                          <div
                            key={activity.id}
                            className={`border rounded-lg p-3 transition-colors ${
                              selectedActivities.includes(activity.id)
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-slate-200'
                            }`}
                          >
                            <div 
                              className="flex items-start justify-between cursor-pointer"
                              onClick={() => {
                                setSelectedActivities(prev =>
                                  prev.includes(activity.id)
                                    ? prev.filter(id => id !== activity.id)
                                    : [...prev, activity.id]
                                );
                              }}
                            >
                              <div>
                                <h4 className="font-medium text-slate-800">{activity.name}</h4>
                                <p className="text-xs text-slate-500 mt-1">
                                  {activity.shift} • {activity.days_of_week.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(', ')}
                                </p>
                              </div>
                              <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                                selectedActivities.includes(activity.id)
                                  ? 'bg-blue-500 border-blue-500'
                                  : 'border-slate-300'
                              }`}>
                                {selectedActivities.includes(activity.id) && (
                                  <CheckCircle className="w-4 h-4 text-white" />
                                )}
                              </div>
                            </div>
                            
                            {selectedActivities.includes(activity.id) && (
                              <div className="mt-2 pt-2 border-t border-slate-200">
                                <label className="flex items-center gap-2 text-xs cursor-pointer">
                                  <input
                                    type="checkbox"
                                    checked={allowRepeatForActivity[activity.id] || false}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      setAllowRepeatForActivity(prev => ({
                                        ...prev,
                                        [activity.id]: e.target.checked
                                      }));
                                    }}
                                    onClick={(e) => e.stopPropagation()}
                                    className="w-4 h-4 text-blue-600 rounded"
                                  />
                                  <span className="text-slate-600">Permitir repetir esta atividade na grade (opcional)</span>
                                </label>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="school">Escola</Label>
                    <Input
                      id="school"
                      data-testid="school-input"
                      value={formData.school}
                      onChange={(e) => setFormData({ ...formData, school: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="neighborhood">Bairro</Label>
                    <Input
                      id="neighborhood"
                      data-testid="neighborhood-input"
                      value={formData.neighborhood}
                      onChange={(e) => setFormData({ ...formData, neighborhood: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="city">Cidade</Label>
                    <Input
                      id="city"
                      data-testid="city-input"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="insertion_reason">Motivo da Inserção</Label>
                  <textarea
                    id="insertion_reason"
                    data-testid="insertion-reason-textarea"
                    className="flex min-h-[60px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.insertion_reason}
                    onChange={(e) => setFormData({ ...formData, insertion_reason: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="guardian_name">Nome do Responsável *</Label>
                    <Input
                      id="guardian_name"
                      data-testid="guardian-name-input"
                      value={formData.guardian_name}
                      onChange={(e) => setFormData({ ...formData, guardian_name: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="guardian_phone">Telefone do Responsável *</Label>
                    <Input
                      id="guardian_phone"
                      data-testid="guardian-phone-input"
                      value={formData.guardian_phone}
                      onChange={(e) => setFormData({ ...formData, guardian_phone: e.target.value })}
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="guardian_relationship">Parentesco do Responsável</Label>
                    <select
                      id="guardian_relationship"
                      data-testid="guardian-relationship-input"
                      className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                      value={showCustomRelationship ? 'Outro' : (standardRelationships.includes(formData.guardian_relationship) ? formData.guardian_relationship : (formData.guardian_relationship ? 'Outro' : ''))}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (value === 'Outro') {
                          setShowCustomRelationship(true);
                          setFormData({ ...formData, guardian_relationship: '' });
                        } else {
                          setShowCustomRelationship(false);
                          setFormData({ ...formData, guardian_relationship: value });
                        }
                      }}
                    >
                      <option value="">Selecione (opcional)</option>
                      <option value="Mãe">Mãe</option>
                      <option value="Pai">Pai</option>
                      <option value="Avó">Avó</option>
                      <option value="Avô">Avô</option>
                      <option value="Tio">Tio</option>
                      <option value="Tia">Tia</option>
                      <option value="Outro">Outro</option>
                    </select>
                    {/* Campo de texto para parentesco customizado */}
                    {showCustomRelationship && (
                      <Input
                        className="mt-2"
                        placeholder="Digite o parentesco"
                        value={formData.guardian_relationship || ''}
                        onChange={(e) => setFormData({ ...formData, guardian_relationship: e.target.value })}
                        data-testid="guardian-relationship-custom"
                      />
                    )}
                  </div>
                  <div>
                    <Label htmlFor="guardian_email">Email do Responsável</Label>
                    <Input
                      id="guardian_email"
                      type="email"
                      data-testid="guardian-email-input"
                      value={formData.guardian_email}
                      onChange={(e) => setFormData({ ...formData, guardian_email: e.target.value })}
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="address">Endereço</Label>
                  <Input
                    id="address"
                    data-testid="child-address-input"
                    value={formData.address}
                    onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                    placeholder="Opcional"
                  />
                </div>

                {/* Relacionamento de Crianças */}
                <div className="border-t pt-4">
                  <Label className="text-base font-semibold">Crianças Relacionadas (Evitar Mesma Atividade/Horário)</Label>
                  <p className="text-xs text-slate-500 mb-3">
                    Selecione até 4 crianças que NÃO devem ficar na mesma atividade e horário que esta criança
                  </p>
                  
                  <div className="space-y-2">
                    {[0, 1, 2, 3].map(index => {
                      const relatedChildren = formData.related_children || [];
                      const selectedChildId = relatedChildren[index];
                      const selectedChildData = children.find(c => c.id === selectedChildId);
                      
                      return (
                        <div key={index} className="flex items-center gap-2">
                          <Label className="text-sm w-24">Criança {index + 1}:</Label>
                          <select
                            className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            value={selectedChildId || ''}
                            onChange={(e) => {
                              const newRelated = [...(formData.related_children || [])];
                              if (e.target.value) {
                                newRelated[index] = e.target.value;
                              } else {
                                newRelated.splice(index, 1);
                              }
                              setFormData({ ...formData, related_children: newRelated.filter(Boolean) });
                            }}
                          >
                            <option value="">Selecione uma criança...</option>
                            {children
                              .filter(c => c.id !== editingChild?.id && !(formData.related_children || []).includes(c.id) || c.id === selectedChildId)
                              .map(child => (
                                <option key={child.id} value={child.id}>
                                  {child.name} ({child.enrollment_number})
                                </option>
                              ))}
                          </select>
                          {selectedChildData && (
                            <span className="text-xs text-slate-500">
                              {selectedChildData.age} anos
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div>
                  <Label htmlFor="medical_info">Informações Relevantes</Label>
                  <textarea
                    id="medical_info"
                    data-testid="medical-info-textarea"
                    className="flex min-h-[80px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                    value={formData.medical_info}
                    onChange={(e) => setFormData({ ...formData, medical_info: e.target.value })}
                    placeholder="Alergias, medicações, observações..."
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" data-testid="save-child-button">
                    {editingChild ? 'Atualizar' : 'Cadastrar'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white p-4 rounded-lg border border-slate-200 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Busca por nome */}
          <div>
            <Label className="text-xs">Buscar por nome</Label>
            <Input
              placeholder="Digite o nome..."
              value={filters.search}
              onChange={(e) => setFilters({...filters, search: e.target.value})}
            />
          </div>

          {/* Origem */}
          <div>
            <Label className="text-xs">Origem</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.origin}
              onChange={(e) => setFilters({...filters, origin: e.target.value})}
            >
              <option value="">Todas</option>
              <option value="livre_demanda">Livre Demanda</option>
              <option value="gov_celso_ramos">Governador Celso Ramos</option>
              <option value="biguacu">Biguaçu</option>
            </select>
          </div>

          {/* Gênero */}
          <div>
            <Label className="text-xs">Gênero</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.gender}
              onChange={(e) => setFilters({...filters, gender: e.target.value})}
            >
              <option value="">Todos</option>
              <option value="masculino">Masculino</option>
              <option value="feminino">Feminino</option>
            </select>
          </div>

          {/* Turno */}
          <div>
            <Label className="text-xs">Turno</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.shift}
              onChange={(e) => setFilters({...filters, shift: e.target.value})}
            >
              <option value="">Todos</option>
              <option value="Matutino">Manhã</option>
              <option value="Vespertino">Tarde</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Dias */}
          <div>
            <Label className="text-xs">Dias de Participação</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.days}
              onChange={(e) => setFilters({...filters, days: e.target.value})}
            >
              <option value="">Todos</option>
              <option value="ter_qui">Ter e Qui</option>
              <option value="qua_sex">Qua e Sexta</option>
              <option value="todos">Todos os dias (Seg-Sex)</option>
            </select>
          </div>

          {/* Filtro por Idade - Múltipla escolha */}
          <div className="md:col-span-2">
            <Label className="text-xs">Idade</Label>
            <div className="flex flex-wrap gap-1 mt-1">
              {[6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(age => (
                <button
                  key={age}
                  type="button"
                  onClick={() => {
                    const newAges = filters.ages.includes(age)
                      ? filters.ages.filter(a => a !== age)
                      : [...filters.ages, age];
                    setFilters({...filters, ages: newAges});
                  }}
                  className={`px-2 py-1 text-xs rounded-md border transition-colors ${
                    filters.ages.includes(age)
                      ? 'bg-blue-500 text-white border-blue-500'
                      : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                  }`}
                >
                  {age}
                </button>
              ))}
            </div>
          </div>

          {/* Botão Limpar */}
          <div className="flex items-end">
            <Button
              variant="outline"
              className="w-full"
              onClick={() => setFilters({
                search: '',
                origin: '',
                gender: '',
                days: '',
                ages: [],
                sortOrder: 'asc',
                sortBy: 'name',
                shift: ''
              })}
            >
              Limpar Filtros
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Ordenar Por */}
          <div>
            <Label className="text-xs">Ordenar Por</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.sortBy}
              onChange={(e) => setFilters({...filters, sortBy: e.target.value})}
            >
              <option value="name">Nome</option>
              <option value="age">Idade</option>
            </select>
          </div>

          {/* Ordem */}
          <div>
            <Label className="text-xs">Ordem</Label>
            <select
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
              value={filters.sortOrder}
              onChange={(e) => setFilters({...filters, sortOrder: e.target.value})}
            >
              <option value="asc">Crescente</option>
              <option value="desc">Decrescente</option>
            </select>
          </div>
        </div>

        <div className="text-sm text-slate-600">
          Mostrando {filteredChildren.length} de {children.length} criança(s)
        </div>
      </div>

      {/* Search */}
      <div className="bg-white rounded-xl shadow-md p-4 border border-slate-100">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
          <Input
            data-testid="search-children-input"
            placeholder="Buscar por nome, responsável ou número de matrícula..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {/* Deactivation Dialog */}
      <Dialog open={deactivateDialogOpen} onOpenChange={setDeactivateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desativar Matrícula</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="deactivation_date">Data da Desativação *</Label>
              <Input
                id="deactivation_date"
                type="date"
                value={deactivationData.deactivation_date}
                onChange={(e) => setDeactivationData({ ...deactivationData, deactivation_date: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="deactivation_reason">Motivo da Desativação *</Label>
              <textarea
                id="deactivation_reason"
                className="flex min-h-[100px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                value={deactivationData.deactivation_reason}
                onChange={(e) => setDeactivationData({ ...deactivationData, deactivation_reason: e.target.value })}
                required
              />
            </div>
            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setDeactivateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleDeactivate} className="bg-red-600 hover:bg-red-700">
                Desativar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Children List */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="children-list">
          {filteredChildren.map((child) => (
            <div
              key={child.id}
              className={`bg-white rounded-xl shadow-md p-4 border card-hover cursor-pointer transition-all hover:shadow-lg overflow-hidden ${
                child.is_active ? 'border-slate-100' : 'border-red-300 bg-red-50'
              }`}
              onClick={() => {
                setViewingChild(child);
                setChildReports([]);
                fetchChildReports(child.id);
                setDetailsDialogOpen(true);
              }}
            >
              {/* Conteúdo do card simplificado */}
              <div className="flex flex-col items-center text-center relative">
                {canEditChildren && unreadReportsChildIds.includes(child.id) && (
                  <div className="absolute top-0 right-0 z-10" title="Relato não lido">
                    <span className="flex h-4 w-4">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-4 w-4 bg-purple-500 items-center justify-center">
                        <MessageSquare className="h-2.5 w-2.5 text-white" />
                      </span>
                    </span>
                  </div>
                )}
                {child.medical_info && child.medical_info.trim() && (
                  <div
                    className={`absolute ${canEditChildren && unreadReportsChildIds.includes(child.id) ? 'top-5' : 'top-0'} left-0 z-10`}
                    title="Possui informações relevantes"
                    data-testid={`info-indicator-${child.id}`}
                  >
                    <Info className="h-4 w-4 text-amber-500 drop-shadow-sm" />
                  </div>
                )}
                <ChildAvatar 
                  photo={child.photo_url} 
                  name={child.name} 
                  size="xl"
                  zoomable={true}
                  className={`border-3 ${child.is_active ? 'border-blue-200' : 'border-red-200'}`}
                />
                <h3 className="font-semibold text-slate-800 mt-3 text-sm">{child.name}</h3>
                <p className="text-xs text-slate-500 mt-1">{child.age} anos</p>
                {!child.is_active && (
                  <span className="text-xs px-2 py-1 bg-red-100 rounded-full text-red-700 mt-2">
                    Inativa
                  </span>
                )}
              </div>
              
              {/* Botões de ação */}
              <div className="flex items-center justify-center gap-1.5 mt-3 pt-3 border-t border-slate-100" onClick={(e) => e.stopPropagation()}>
                {/* Botão de relato - disponível para todos */}
                <button
                  className="p-1.5 rounded-md text-purple-600 hover:bg-purple-50 border border-slate-200 transition-colors"
                  onClick={(e) => {
                    e.stopPropagation();
                    setViewingChild(child);
                    setReportDialogOpen(true);
                  }}
                  title="Escrever relato"
                  data-testid={`report-child-${child.id}`}
                >
                  <MessageSquare className="h-3.5 w-3.5" />
                </button>
                
                {/* Botões de edição - apenas com permissão */}
                {canEditChildren && child.is_active && (
                  <>
                    <button
                      className="p-1.5 rounded-md text-slate-600 hover:bg-slate-50 border border-slate-200 transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleEdit(child); }}
                      data-testid={`edit-child-${child.id}`}
                      title="Editar"
                    >
                      <Edit className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="p-1.5 rounded-md text-orange-500 hover:bg-orange-50 border border-slate-200 transition-colors"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChild(child);
                        setDeactivateDialogOpen(true);
                      }}
                      data-testid={`deactivate-child-${child.id}`}
                      title="Inativar"
                    >
                      <XCircle className="h-3.5 w-3.5" />
                    </button>
                    <button
                      className="p-1.5 rounded-md text-red-500 hover:bg-red-50 border border-slate-200 transition-colors"
                      onClick={(e) => { e.stopPropagation(); handleDelete(child.id); }}
                      data-testid={`delete-child-${child.id}`}
                      title="Excluir"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </>
                )}
                
                {canEditChildren && !child.is_active && (
                  <button
                    className="p-1.5 rounded-md text-green-600 hover:bg-green-50 border border-slate-200 transition-colors"
                    onClick={(e) => { e.stopPropagation(); handleReactivate(child.id); }}
                    data-testid={`reactivate-child-${child.id}`}
                    title="Reativar"
                  >
                    <CheckCircle className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {!loading && filteredChildren.length === 0 && (
        <div className="bg-white rounded-xl shadow-md p-12 text-center border border-slate-100">
          <User className="h-16 w-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600">Nenhuma criança encontrada</p>
        </div>
      )}

      {/* Modal de Detalhes da Criança */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              {viewingChild && (
                <>
                  <ChildAvatar 
                    photo={viewingChild.photo_url} 
                    name={viewingChild.name} 
                    size="lg"
                    zoomable={true}
                    className={`border-2 ${viewingChild.is_active ? 'border-blue-200' : 'border-red-200'}`}
                  />
                  <div>
                    <span className="text-lg">{viewingChild.name}</span>
                    <p className="text-sm font-normal text-slate-500">
                      {viewingChild.age} anos • {viewingChild.is_active ? 'Ativo' : 'Inativo'}
                    </p>
                  </div>
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {viewingChild && (
            <div className="space-y-4 mt-2">
              {/* Informações básicas */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Matrícula</p>
                  <p className="font-medium text-slate-800">{viewingChild.enrollment_number}</p>
                </div>
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Gênero</p>
                  <p className="font-medium text-slate-800">
                    {viewingChild.gender?.toUpperCase() === 'MASCULINO' ? 'Masculino' : 'Feminino'}
                  </p>
                </div>
              </div>

              {/* Data de Nascimento */}
              <div className="flex items-start gap-3 p-3 bg-purple-50 rounded-lg">
                <Calendar className="h-5 w-5 text-purple-500 mt-0.5" />
                <div>
                  <p className="text-xs text-purple-600 uppercase tracking-wide">Data de Nascimento</p>
                  <p className="font-medium text-slate-800">
                    {viewingChild.birth_date ? new Date(viewingChild.birth_date + 'T00:00:00').toLocaleDateString('pt-BR') : '-'}
                  </p>
                </div>
              </div>

              {/* Responsável */}
              <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <Users className="h-5 w-5 text-blue-500 mt-0.5" />
                <div>
                  <p className="text-xs text-blue-600 uppercase tracking-wide">Responsável</p>
                  <p className="font-medium text-slate-800">
                    {viewingChild.guardian_name || '-'}
                  </p>
                  {viewingChild.guardian_relationship && (
                    <p className="text-sm text-slate-600">
                      ({viewingChild.guardian_relationship})
                    </p>
                  )}
                  {canEditChildren && viewingChild.guardian_phone && (
                    <p className="text-sm text-slate-600 flex items-center gap-1 mt-1">
                      <Phone className="h-3 w-3" />
                      {viewingChild.guardian_phone}
                    </p>
                  )}
                </div>
              </div>

              {/* Turno e Dias */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-amber-50 rounded-lg">
                  <p className="text-xs text-amber-600 uppercase tracking-wide">Turno</p>
                  <p className="font-medium text-slate-800">
                    {viewingChild.shift === 'Matutino' ? 'Manhã' : 'Tarde'}
                  </p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg">
                  <p className="text-xs text-green-600 uppercase tracking-wide">Dias</p>
                  <p className="font-medium text-slate-800 text-sm">
                    {viewingChild.days_of_week?.map(day => 
                      day === 'segunda' ? 'Seg' : 
                      day === 'terca' ? 'Ter' :
                      day === 'quarta' ? 'Qua' :
                      day === 'quinta' ? 'Qui' :
                      day === 'sexta' ? 'Sex' : day
                    ).join(', ') || '-'}
                  </p>
                </div>
              </div>

              {/* Origem */}
              <div className="p-3 bg-indigo-50 rounded-lg">
                <p className="text-xs text-indigo-600 uppercase tracking-wide">Origem da Matrícula</p>
                <p className="font-medium text-slate-800">
                  {viewingChild.enrollment_origin === 'livre_demanda' ? 'Livre Demanda' :
                   viewingChild.enrollment_origin === 'gov_celso_ramos' ? 'Gov. Celso Ramos' :
                   viewingChild.enrollment_origin === 'biguacu' ? 'Biguaçu' : viewingChild.enrollment_origin || '-'}
                </p>
              </div>

              {/* Endereço e Bairro */}
              {(viewingChild.neighborhood || viewingChild.address) && (
                <div className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                  <MapPin className="h-5 w-5 text-slate-400 mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide">Endereço</p>
                    {viewingChild.address && (
                      <p className="font-medium text-slate-800 text-sm">{viewingChild.address}</p>
                    )}
                    {viewingChild.neighborhood && (
                      <p className="text-sm text-slate-600">{viewingChild.neighborhood}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Escola - apenas para quem pode editar */}
              {canEditChildren && viewingChild.school && (
                <div className="flex items-start gap-3 p-3 bg-cyan-50 rounded-lg">
                  <BookOpen className="h-5 w-5 text-cyan-500 mt-0.5" />
                  <div>
                    <p className="text-xs text-cyan-600 uppercase tracking-wide">Escola</p>
                    <p className="font-medium text-slate-800">{viewingChild.school}</p>
                  </div>
                </div>
              )}

              {/* Informações Relevantes */}
              <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
                <div className="flex items-center gap-2 mb-1">
                  <Info className="h-4 w-4 text-amber-600" />
                  <p className="text-xs text-amber-700 uppercase tracking-wide font-medium">
                    Informações Relevantes
                  </p>
                </div>
                <p className="text-sm text-slate-700">
                  {viewingChild.medical_info || 'Nenhuma informação relevante registrada'}
                </p>
              </div>

              {/* Relatos da Criança */}
              <div className="p-3 bg-purple-50 rounded-lg border border-purple-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-purple-600" />
                    <p className="text-xs text-purple-600 uppercase tracking-wide font-medium">Relatos</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        fetchChildReports(viewingChild.id);
                      }}
                      className="text-xs text-purple-600 hover:text-purple-800 underline"
                      data-testid="view-child-reports"
                    >
                      {childReports.length > 0 ? `Ver (${childReports.length})` : 'Carregar'}
                    </button>
                    <button
                      onClick={() => setReportDialogOpen(true)}
                      className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded hover:bg-purple-700"
                      data-testid="write-report-from-detail"
                    >
                      + Novo
                    </button>
                  </div>
                </div>
                {childReports.length > 0 ? (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {childReports.map(report => (
                      <div key={report.id} className="p-2 bg-white rounded border border-purple-100 text-sm">
                        <p className="text-slate-700 whitespace-pre-wrap text-xs">{report.content}</p>
                        <div className="flex items-center justify-between mt-1">
                          <span className="text-xs text-slate-400">
                            {report.created_by} • {new Date(report.created_at).toLocaleDateString('pt-BR')}
                          </span>
                          {(report.created_by_id === viewingChild?.id || canEditChildren) && (
                            <button
                              onClick={() => deleteReport(report.id)}
                              className="text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="h-3 w-3" />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-xs text-purple-400 italic">Nenhum relato registrado</p>
                )}
              </div>

              {/* Motivo da Inativação (se inativo) */}
              {!viewingChild.is_active && viewingChild.deactivation_reason && (
                <div className="p-3 bg-slate-100 rounded-lg">
                  <p className="text-xs text-slate-500 uppercase tracking-wide">Motivo da Inativação</p>
                  <p className="text-sm text-slate-700">
                    {viewingChild.deactivation_reason}
                  </p>
                  {viewingChild.deactivation_date && (
                    <p className="text-xs text-slate-500 mt-1">
                      Em {new Date(viewingChild.deactivation_date).toLocaleDateString('pt-BR')}
                    </p>
                  )}
                </div>
              )}

              {/* Botão de Editar */}
              {canEditChildren && viewingChild.is_active && (
                <div className="pt-2 border-t border-slate-100">
                  <Button
                    className="w-full bg-[#D87A4E] hover:bg-[#c26a42]"
                    onClick={() => {
                      setDetailsDialogOpen(false);
                      handleEdit(viewingChild);
                    }}
                  >
                    <Edit className="h-4 w-4 mr-2" />
                    Editar Cadastro
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para escrever relato */}
      <Dialog open={reportDialogOpen} onOpenChange={setReportDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-purple-600" />
              Novo Relato
            </DialogTitle>
          </DialogHeader>
          
          {viewingChild && (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <ChildAvatar photo={viewingChild.photo_url} name={viewingChild.name} size="md" />
                <div>
                  <p className="font-medium text-slate-800">{viewingChild.name}</p>
                  <p className="text-sm text-slate-500">{viewingChild.age} anos</p>
                </div>
              </div>
              
              <div>
                <Label htmlFor="report-content">Escreva o relato</Label>
                <textarea
                  id="report-content"
                  className="flex min-h-[150px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm mt-1"
                  value={reportContent}
                  onChange={(e) => setReportContent(e.target.value)}
                  placeholder="Descreva a situação, comportamento ou observação sobre a criança..."
                />
              </div>
              
              <p className="text-xs text-slate-500">
                Este relato será visível para você e para os administradores/editores.
              </p>
              
              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={() => setReportDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button 
                  onClick={submitReport}
                  className="bg-purple-600 hover:bg-purple-700"
                >
                  Salvar Relato
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal para ver todos os relatos (editores) */}
      <Dialog open={reportsListOpen} onOpenChange={setReportsListOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bell className="w-5 h-5 text-purple-600" />
              Relatos das Crianças
            </DialogTitle>
          </DialogHeader>
          
          <ReportsList 
            onMarkRead={markReportAsRead}
            onDelete={deleteReport}
            fetchUnreadCount={fetchUnreadReportsCount}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Componente separado para lista de relatos
const ReportsList = ({ onMarkRead, onDelete, fetchUnreadCount }) => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  useEffect(() => {
    fetchReports();
  }, []);

  const fetchReports = async () => {
    try {
      const response = await axios.get(`${API}/children/reports/all`);
      setReports(response.data || []);
    } catch (error) {
      console.log('Erro ao carregar relatos');
    } finally {
      setLoading(false);
    }
  };

  const handleMarkRead = async (reportId) => {
    await onMarkRead(reportId);
    // Atualizar localmente
    setReports(prev => prev.map(r => 
      r.id === reportId 
        ? { ...r, read_by: [...(r.read_by || []), user.username] }
        : r
    ));
  };

  const handleDelete = async (reportId) => {
    await onDelete(reportId);
    setReports(prev => prev.filter(r => r.id !== reportId));
  };

  if (loading) {
    return <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div></div>;
  }

  if (reports.length === 0) {
    return (
      <div className="text-center py-8 text-slate-500">
        <MessageSquare className="h-12 w-12 mx-auto mb-3 text-slate-300" />
        <p>Nenhum relato encontrado</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {reports.map((report) => {
        const isUnread = !(report.read_by || []).includes(user.username);
        const isAuthor = report.created_by_id === user.id;
        
        return (
          <div 
            key={report.id} 
            className={`p-4 rounded-lg border ${isUnread ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200'}`}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="font-semibold text-slate-800">{report.child_name}</span>
                  {isUnread && (
                    <span className="text-xs bg-purple-600 text-white px-2 py-0.5 rounded-full">Novo</span>
                  )}
                </div>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{report.content}</p>
                <div className="flex items-center gap-3 mt-2 text-xs text-slate-500">
                  <span>Por: {report.created_by}</span>
                  <span>•</span>
                  <span>{new Date(report.created_at).toLocaleDateString('pt-BR')} às {new Date(report.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}</span>
                </div>
              </div>
              
              <div className="flex items-center gap-1">
                {isUnread && (
                  <button
                    onClick={() => handleMarkRead(report.id)}
                    className="p-1.5 text-purple-600 hover:bg-purple-100 rounded"
                    title="Marcar como lido"
                  >
                    <CheckCircle className="h-4 w-4" />
                  </button>
                )}
                {(isAuthor || user.is_admin || user.is_superadmin) && (
                  <button
                    onClick={() => handleDelete(report.id)}
                    className="p-1.5 text-red-600 hover:bg-red-100 rounded"
                    title="Excluir relato"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Children;